import { useQuery } from '@tanstack/react-query'
import { Briefcase, ChevronDown, GraduationCap, KeyRound, Mail, Pencil, Phone, UserCheck } from 'lucide-react'
import type { TFunction } from 'i18next'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link, useParams } from 'react-router'
import { ApiError } from '@/api/errors'
import { ErrorState, LoadingState, NotFoundState } from '@/components/feedback/States'
import { Avatar } from '@/components/ui/Avatar'
import { Badge } from '@/components/ui/Badge'
import { Button } from '@/components/ui/Button'
import { Card, CardBody, CardHeader } from '@/components/ui/Card'
import { DescriptionList } from '@/components/ui/DescriptionList'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/DropdownMenu'
import { Breadcrumbs } from '@/components/ui/PageHeader'
import { Can } from '@/features/auth/guards'
import { useAuth } from '@/features/auth/useAuth'
import { staffQuery } from '@/features/staff/api'
import { StaffAccountDialog, StaffStatusDialog } from '@/features/staff/components/StaffDialogs'
import { staffStatusTone } from '@/features/staff/status'
import { useDocumentTitle } from '@/hooks/useDocumentTitle'
import { ageFrom, formatDate, formatMoney, humanize } from '@/utils/format'

function tenure(joined: string | null, t: TFunction<'staff'>): string {
  if (!joined) return '—'
  const months = Math.max(
    0,
    Math.floor((Date.now() - new Date(`${joined}T00:00:00`).getTime()) / (30.44 * 86_400_000)),
  )
  const y = Math.floor(months / 12)
  const m = months % 12
  return (
    [y ? t('{{count}} yr', { count: y }) : '', m ? t('{{count}} mo', { count: m }) : '']
      .filter(Boolean)
      .join(' ') || t('Less than a month')
  )
}

export function StaffDetailPage() {
  const id = Number(useParams().id)
  const { data: m, isPending, error, refetch } = useQuery(staffQuery(id))
  const { t } = useTranslation('staff')
  const { can, school } = useAuth()
  const [dialog, setDialog] = useState<'status' | 'account' | null>(null)
  useDocumentTitle(m?.fullName ?? 'Staff')

  if (isPending) return <LoadingState />
  if (error)
    return error instanceof ApiError && error.status === 404 ? (
      <NotFoundState resource="staff member" homeHref="/admin/staff" />
    ) : (
      <ErrorState onRetry={() => void refetch()} />
    )

  const age = ageFrom(m.dateOfBirth)
  const close = (open: boolean) => (open ? undefined : setDialog(null))

  return (
    <>
      <Breadcrumbs
        homeHref="/admin"
        items={[{ label: t('Staff'), to: '/admin/staff' }, { label: m.fullName }]}
      />
      <Card className="mt-3 overflow-hidden">
        <div className="bg-brand-gradient h-24 sm:h-28" aria-hidden="true" />
        <div className="flex flex-col gap-4 px-5 pb-5 sm:flex-row sm:items-end sm:px-6">
          <Avatar
            name={m.fullName}
            src={m.photoUrl}
            size="xl"
            className="-mt-10 ring-4 ring-surface sm:-mt-12"
          />
          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-center gap-2">
              <h1 className="text-xl font-semibold sm:text-2xl">{m.fullName}</h1>
              <Badge tone={staffStatusTone[m.status]} dot>
                {humanize(m.status)}
              </Badge>
            </div>
            <p className="mt-1 flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-muted">
              <span className="font-mono text-xs">{m.employeeCode}</span>
              {m.designation ? <span>{m.designation.name}</span> : null}
              {m.department ? <span>{m.department.name}</span> : null}
            </p>
          </div>
          <Can permission="staff.manage">
            <div className="flex flex-wrap gap-2">
              <Button asChild variant="secondary" leftIcon={<Pencil />}>
                <Link to={`/admin/staff/${id}/edit`}>{t('Edit')}</Link>
              </Button>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button rightIcon={<ChevronDown />}>{t('Actions')}</Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent>
                  <DropdownMenuItem icon={<UserCheck />} onSelect={() => setDialog('status')}>
                    {t('Change status')}
                  </DropdownMenuItem>
                  {can('users.manage') ? (
                    <DropdownMenuItem
                      icon={<KeyRound />}
                      onSelect={() => setDialog('account')}
                      disabled={m.account !== null}
                    >
                      {m.account ? t('Login already exists') : t('Create a login')}
                    </DropdownMenuItem>
                  ) : null}
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </Can>
        </div>
      </Card>

      <div className="mt-6 grid gap-6 lg:grid-cols-[minmax(0,1fr)_20rem]">
        <div className="space-y-6">
          <Card>
            <CardHeader title={t('Employment')} icon={<Briefcase />} />
            <CardBody>
              <DescriptionList
                items={[
                  { label: t('Designation'), value: m.designation?.name ?? '—' },
                  { label: t('Department'), value: m.department?.name ?? '—' },
                  { label: t('Employment type'), value: humanize(m.employmentType) },
                  { label: t('Main subject'), value: m.primarySubject?.name ?? '—' },
                  {
                    label: t('Joined'),
                    value: m.joiningDate ? `${formatDate(m.joiningDate)} · ${tenure(m.joiningDate, t)}` : '—',
                  },
                  { label: t('Left'), value: formatDate(m.leavingDate) },
                  ...(m.basicSalary !== null
                    ? [{ label: t('Basic salary'), value: formatMoney(m.basicSalary, school?.currency) }]
                    : []),
                  { label: t('Qualification'), value: m.qualification ?? '—' },
                ]}
              />
            </CardBody>
          </Card>
          <Card>
            <CardHeader title={t('Personal details')} />
            <CardBody>
              <DescriptionList
                items={[
                  { label: t('Gender'), value: humanize(m.gender) },
                  {
                    label: t('Date of birth'),
                    value: m.dateOfBirth
                      ? `${formatDate(m.dateOfBirth)}${age !== null ? ` (${t('{{count}} yr', { count: age })})` : ''}`
                      : '—',
                  },
                  { label: t('Blood group'), value: m.bloodGroup ?? '—' },
                  {
                    label: t('Address'),
                    value: m.address ? <span className="whitespace-pre-line">{m.address}</span> : '—',
                    span: 2,
                  },
                ]}
              />
            </CardBody>
          </Card>
        </div>
        <div className="space-y-6">
          <Card>
            <CardHeader title={t('Contact')} />
            <CardBody className="space-y-3 text-sm">
              {m.phone ? (
                <a
                  href={`tel:${m.phone}`}
                  className="flex items-center gap-2.5 text-ink-soft hover:text-brand-700"
                >
                  <Phone className="size-4 text-subtle" aria-hidden="true" /> {m.phone}
                </a>
              ) : null}
              {m.email ? (
                <a
                  href={`mailto:${m.email}`}
                  className="flex items-center gap-2.5 break-all text-ink-soft hover:text-brand-700"
                >
                  <Mail className="size-4 text-subtle" aria-hidden="true" /> {m.email}
                </a>
              ) : null}
              {!m.phone && !m.email ? <p className="text-muted">{t('No contact details.')}</p> : null}
            </CardBody>
          </Card>
          <Card>
            <CardHeader title={t('Class teacher of')} icon={<GraduationCap />} />
            <CardBody>
              {m.classTeacherOf.length ? (
                <div className="flex flex-wrap gap-2">
                  {m.classTeacherOf.map((s) => (
                    <Badge key={s.id} tone="brand">
                      {s.name}
                    </Badge>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-muted">{t('Not a class teacher.')}</p>
              )}
            </CardBody>
          </Card>
          <Card>
            <CardHeader title={t('Platform access')} icon={<KeyRound />} />
            <CardBody className="text-sm">
              {m.account ? (
                <div className="space-y-1">
                  <p className="font-medium text-ink">{m.account.email}</p>
                  <p className="text-muted">
                    {t('Role: {{role}}', { role: humanize(m.account.role) })}{' '}
                    {!m.account.isActive ? (
                      <Badge tone="danger" size="sm" className="ml-1">
                        {t('Disabled')}
                      </Badge>
                    ) : null}
                  </p>
                </div>
              ) : (
                <p className="text-muted">{t('No login yet.')}</p>
              )}
            </CardBody>
          </Card>
        </div>
      </div>

      {dialog === 'status' ? <StaffStatusDialog member={m} open onOpenChange={close} /> : null}
      {dialog === 'account' ? <StaffAccountDialog member={m} open onOpenChange={close} /> : null}
    </>
  )
}
